import PropTypes from "prop-types";
import { ReduceStore } from "flux/utils";
import typeCheckingStore from "data/util/typeCheckingStore";
import Dispatcher from "Dispatcher";
import EntityProxy from "data/util/EntityProxy";
import { recordIdType } from "data/util/typedefs";
import MappingActions from "data/MappingActions";
import TableStore from "data/TableStore";
import { getMappingPlugins } from "data/selectors/entities";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import invariant from "invariant";
import DataManager from "data/DataManager";
import userStore from "data/userStore";
import LoadObject from "data/LoadObject";
import dotProp from "dot-prop-immutable";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import entityMetaStore from "data/meta/entityMetaStore";
import SuperRecordActions from "data/SuperRecordActions";
import SearchResultActions from "data/SearchResultActions";
import SearchResultStore from "data/SearchResultStore";
import SuperRecordStore from "data/SuperRecordStore";
import loadObjectOf from "data/util/loadObjectOf";
import MapButtonActions from "data/MapButtonActions";
import MapDialogActions from "data/MapDialogActions";
import ClientId from "data/ClientId";
import EntityName from "data/util/EntityName";
import { saveSuperRecord } from "data/util/saveit";
import safeDispatch from "data/util/safeDispatch";
import MappingWorkflowVisitor from "data/MappingWorkflowVisitor";
import SuperRecordListActions from "data/SuperRecordListActions";
import StereotypesStore from "data/StereotypesStore";
import MergeActions from "data/MergeActions";
import MergeStore from "data/MergeStore";
import { buildMappingStateFromMergeState } from "data/util/merge/mergeHelpers";
import { buildMergeExclusions } from "data/util/merge/buildMergeExclusions";
import { isConfirmed } from "./OverStore";
import { MERGE_PLUGINS } from "./MergeStore";
/*
I am NOT contexted, even though I operate w/in a contexted scope. This is due to
the fact that all mapping operations are modal, so there can never be more
than one active at a time. The context to operate within is supplied by the
initiating action
 */

const ENTIRE_FIELD = "__ENTIRE_FIELD";

const selectSource = (state, source) => {
    invariant(state.target, "Can't select a source w/o a target already selected");
    return {
        ...state,
        source,
        modelLO: LoadObject.loading(DataManager.loadMappingModel(
            userStore.getActiveOrgKey(),
            state.target.entityKey,
            source.entityKey,
        )),
    };
};

/**
 * I execute a callback on each id pair in the passed state. The callback will
 * be invoked with three arguments: a LoadObject containing a {id, key, global}
 * type, the source record id, and the entity-qualified field key of the field
 * it's for (e.g., "ob/1/episode.newborns").
 * @param state
 * @param callback
 */
const forEachIdPair = (state, callback) => {
    if (!state.hasOwnProperty("idPairs")) return;
    for (const sid of Object.keys(state.idPairs)) {
        const fields = state.idPairs[sid];
        for (const rfk of Object.keys(fields)) {
            callback(fields[rfk], sid, rfk);
        }
    }
};

const mapIdPairs = (state, callback) => {
    forEachIdPair(state, (curr, sid, rfk) => {
        const next = callback(curr, sid, rfk);
        if (curr !== next) {
            state = dotProp.set(
                state,
                ["idPairs", sid, rfk],
                next,
            );
        }
    });
    return state;
};

export const ERR_NO_MATCH_FOUND = "No matching records were found.";
export const ERR_MULTIPLE_MATCHES_FOUND = "Multiple matching records were found.";

const doItAgain = reason => {
    invariant(Dispatcher.isDispatching(), "doItAgain only makes sense during dispatch");
    safeDispatch({
        type: MappingActions.__DO_IT_AGAIN,
        reason,
    });
};

const isReady = state => {
    // If there's no mapping active, I'm not ready
    if (!state.active) return false;

    // If there's no model loading, I'm not ready
    if (state.modelLO == null) return false;

    // If the model's loaded, I'm ready!
    return state.modelLO.hasValue();
};

const __workflow = state => {
    // If I'm not ready, do nothing
    if (!isReady(state)) return state;

    // register the helper, if needed
    if (!state.interactive && state.helperToken == null) {
        state = {
            ...state,
            helperToken: Dispatcher.register(__workflow_helper),
        };
        doItAgain("registered workflow helper");
    }

    Dispatcher.waitFor([ // todo: get this.dispatcher...
        SearchResultStore.getDispatchToken(),
        /*
         * I might need TableStore to process certain actions before I can do
         * my job, but generally it's exactly the reverse. So I'll just detect
         * when the order's backwards, and call doItAgain. This will
         * occasionally introduce a handful-of-milliseconds delay. But Flux is
         * well worth it, especially since it'll only be incurred in the face
         * of API requests which are much slower than that.
         */
    ]);

    const visitor = new MappingWorkflowVisitor(state)
        .execute();
    state = visitor.getState();
    if (visitor.isDoItAgain()) {
        doItAgain(visitor.getDoItAgainReason());
    }

    // all we _really_ need to do is change state, but the counter is useful for
    // detecting/breaking infinite loops. :)
    return {
        ...state,
        _counter: (state._counter || 0) + 1,
    };
};

const __workflow_helper = action => {
    Dispatcher.waitFor([ // todo: get this.dispatcher...
        SuperRecordStore.getDispatchToken(),
        TableStore.getDispatchToken(),
        rawStore.getDispatchToken(),
    ]);

    const state = rawStore.getState();
    const bail = process.env.NODE_ENV === "production"
        ? () => { /* noop */ }
        : (fmt, ...args) => {
            // console.log itself supports format strings, but it doesn't expand
            // arrays when interpolating, so do it manually.
            args.unshift(state._counter);
            let argIndex = 0;
            console.log(("MAP(%s)> " + fmt).replace(/%s/g, function () {
                return args[argIndex++];
            }));
        };

    // If the workflow didn't run, ignore the action.
    if (!rawStore.hasChanged()) {
        return bail("Workflow didn't run");
    }

    // Prevent infinite loops in development.
    if (process.env.NODE_ENV !== "production") {
        invariant(
            state._counter < 50,
            "Mapping went through the workflow %s times?!",
            state._counter,
        );
    }

    // If we're not active, something is very wrong.
    invariant(
        state.active,
        "Mapping workflow helper is wired with no active mapping?!",
    );

    // If we're interactive, something is wrong.
    invariant(
        !state.interactive,
        "Mapping workflow helper is wired while interactive?!",
    );

    // If TableStore changed, idPairs might be stale, so re-initiate and bail.
    if (TableStore.hasChanged()) {
        doItAgain("TableStore changed");
        return bail("TableStore changed");
    }

    // If TableStore is waiting for any lists, bail.
    if (TableStore.isWaiting()) {
        return bail("TableStore is waiting");
    }

    // If any idPairs are pending (load OR create!), bail and wait for them.
    const pendingOps = [];
    forEachIdPair(state, lo => {
        if (lo.hasOperation()) {
            pendingOps.push(lo.getOperation());
        }
    });
    if (pendingOps.length > 0) return bail("pending ops: %s", pendingOps);

    // Find any globals that need to be created.
    const toFlush = [];
    forEachIdPair(state, lo => {
        if (!lo.hasValue()) return;
        const t = lo.getValueEnforcing();
        if (t.global && ClientId.is(t.id)) toFlush.push(t);
    });

    // Sanity check that none are currently flushing...
    if (process.env.NODE_ENV !== "production") {
        for (const g of toFlush) {
            const lo = SuperRecordStore.getRecordLO(g.key, g.id);
            if (!lo.isDone()) console.warn("SuperRecord and Mapping are out of sync regarding %s#%s.", g.key, g.id);
        }
    }

    // If any exist, flush one, bail, and wait for it.
    if (toFlush.length > 0) {
        const g = toFlush.find(g =>
            StereotypesStore.getStereotypeLO(g.key).isDone());
        if (g == null) {
            // every single one is waiting for stereotypes
            return bail("stereotype/%s loading", toFlush[0].key);
        }
        saveSuperRecord(g.key, g.id, state.initiatingContext);
        return bail("flush %s#%s initiated", g.key, g.id);
    }

    // DONE!
    Dispatcher.unregister(state.helperToken);

    if(Array.isArray(state.queue) && state.queue.length) {
        // if state.queue.length ... but wait there is more to do!
        safeDispatch({
            type: MappingActions.__PROCESS_NEXT_IN_QUEUE,
        });
        return bail("More items in the mapping queue to process.");
    }

    safeDispatch({
        type: MappingActions.IT_IS_DONE,
        trigger: action.type.toString(),
        initiatingContext: state.initiatingContext,
    });
};

const getPairPath = (sourceId, targetRecordKey, targetFieldKey) => {
    invariant(sourceId != null, "You must pass a non-null sourceId");
    invariant(targetRecordKey, "You must pass a non-null targetRecordKey");
    invariant(targetFieldKey, "You must pass a non-null targetFieldKey");
    const targetKey = `${targetRecordKey}.${targetFieldKey}`;
    return [
        "idPairs",
        sourceId,
        targetKey,
    ];
};

// You probably don't want this one, but rather getPairedIdLO (below)
export const getPairLO = (state, sourceId, targetRecordKey, targetFieldKey) =>
    dotProp.get(
        state,
        getPairPath(sourceId, targetRecordKey, targetFieldKey)
    ) || LoadObject.empty();

export const setPairLO = (state, sourceId, targetRecordKey, targetFieldKey, lo) => {
    invariant(lo != null, "You must pass a non-null LoadObject");
    return dotProp.set(
        state,
        getPairPath(sourceId, targetRecordKey, targetFieldKey),
        lo,
    );
};

export const getPairedIdLO = (state, sourceId, targetRecordKey, targetFieldKey) =>
    getPairLO(state, sourceId, targetRecordKey, targetFieldKey)
        .map(v => v.id);

/*
 * I return a "don't care where" paired id LO, and am questionable. While
 * idPairs are inherently bound to a location in the target tree (via the target
 * entity and field keys), this method ignores that information and acts as if
 * every source record maps onto exactly one target record. When that tenet is
 * violated, an exception will be raised, so it's up to calling code to decide
 * if it's safe to call or not (based on the mapping structure). Note that the
 * exception is raised on a "best effort" basis and should not be relied upon
 * for program correctness.
 */
export const getPairedIdLO_unqualified = (state, sourceId) => {
    let obj = state.idPairs;
    if (obj == null) return LoadObject.empty();
    obj = obj[sourceId];
    if (obj == null) return LoadObject.empty();
    const keys = Object.keys(obj);
    if (keys.length === 0) return LoadObject.empty();
    if (keys.length === 1) return obj[keys[0]].map(v => v.id);
    // if there are many, but they all give the same answer, that's good enough
    const ids = new Set(keys.map(k =>
        obj[k].getValueEnforcing().id));
    if (ids.size === 1) {
        // thanks IE
        let theId;
        ids.forEach(id => theId = id);
        return LoadObject.withValue(theId);
    }
    throw new Error(`Ambiguous unqualified idPair query for ${sourceId}`);
};

function requireContext(action) {
    const fluxContext = action[ACTION_CONTEXT_KEY];
    invariant(fluxContext, "mapping must be initiated w/ a contexted dispatch");
    return fluxContext;
}

class MappingStore extends ReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return {
            active: false,
        };
    }

    reduce(state, action) {
        switch (action.type) {

            case MapButtonActions.LAUNCH_DIALOG: {
                const fluxContext = requireContext(action);
                const plugins = getMappingPlugins(action.target, fluxContext);
                // start from scratch
                return {
                    active: true,
                    initiatingContext: fluxContext,
                    interactive: true,
                    pluginKey: plugins[0].get("id"), // default to the first one
                    target: action.target,
                    drawerOpen: true,
                    exclusions: {},
                };
            }

            case MapDialogActions.SELECT_PLUGIN: {
                return {
                    ...state,
                    pluginKey: action.key,
                };
            }

            case MapDialogActions.SELECT_SOURCE: {
                return selectSource(state, action.source);
            }

            case MergeActions.LAUNCH_DIALOG: {
                this.__dispatcher.waitFor([
                    MergeStore.getDispatchToken(),
                ]);
                const mergeState = MergeStore.getState();
                if(mergeState.source != null) {
                    let nextState = buildMappingStateFromMergeState(mergeState);
                    nextState.initiatingContext = requireContext(action);
                    nextState = __workflow(nextState);
                    nextState.exclusions = buildMergeExclusions(
                        nextState
                    );
                    return nextState;
                }
                return state;
            }

            case MergeActions.SELECT_SOURCE: {
                this.__dispatcher.waitFor([
                    MergeStore.getDispatchToken(),
                ]);
                const nextState = __workflow(buildMappingStateFromMergeState(MergeStore.getState()));
                nextState.exclusions = buildMergeExclusions(
                    nextState
                );
                return nextState;
            }

            case MergeActions.DO_MERGE: {
                if(!isConfirmed(action)) return state;
                this.__dispatcher.waitFor([
                    MergeStore.getDispatchToken(),
                ]);
                return __workflow({
                    ...state,
                    active: true,
                    initiatingContext: requireContext(action),
                    interactive: false,
                });
            }

            case MapButtonActions.AUTOMAP_FROM: {
                // start from scratch
                return __workflow(selectSource({
                    active: true,
                    initiatingContext: requireContext(action),
                    target: action.target,
                }, action.source));
            }

            case MapButtonActions.AUTOMAP_TO_NEW_MAGIC_CHILD: {
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                ]);
                // start from scratch
                state = __workflow(selectSource({
                    active: true,
                    initiatingContext: requireContext(action),
                    target: TableStore.getEntityProxy(
                        action.entityKey,
                        action.newId,
                    ),
                }, action.source));
                doItAgain("TableStore race w/ AUTOMAP_TO_NEW_MAGIC_CHILD");
                return state;
            }

            case MappingActions.MODEL_LOADED: {
                state = {
                    ...state,
                    // don't maintain the promise...
                    modelLO: LoadObject.withValue(null),
                };
                if (state.interactive) {
                    state.drawerOpen = false;
                }
                return __workflow(state);
            }

            case MappingActions.MODEL_LOAD_ERROR: {
                return {
                    ...state,
                    // don't maintain the promise...
                    modelLO: LoadObject.withError(action.error),
                };
            }

            case MapDialogActions.TOGGLE_DRAWER: {
                return {
                    ...state,
                    drawerOpen: !state.drawerOpen,
                };
            }

            case MapDialogActions.TOGGLE_EXCLUSION: {
                return __workflow(toggleExclusion(
                    state,
                    action.recordId,
                    action.columnKey,
                    action.itemKey,
                ));
            }

            case MapDialogActions.SET_MAGIC_ROOT_EXCLUSION: {
                const ei = entityMetaStore.getInfo(state.target.entityKey);
                if (ei.magicRootPointerKey == null) {
                    // there's no MR...
                    console.warn("Magic root fields' exclusion was set, but there's no magic root?!");
                    return state;
                }
                const mr = state.target.get(ei.magicRootPointerKey);
                const infos = entityFieldMetaStore.getAllInfos(mr.entityKey);
                for (const i of infos) {
                    state = setExclusion(state, mr.id, i.logicalColumn, null, action.excluded);
                }
                return __workflow(state);
            }

            case MapDialogActions.IMPORT: {
                return __workflow({
                    ...state,
                    interactive: false,
                });
            }

            case SearchResultActions.SEARCH_RESULT:
            case SuperRecordActions.LOADED:
            case SuperRecordListActions.LOADED: {
                if (!state.active) return state;
                return __workflow(state);
            }

            case SuperRecordActions.START_CREATE: {
                if (!state.active) return state;
                state = mapIdPairs(state, lo => {
                    if (!lo.hasValue()) return lo;
                    if (lo.getValueEnforcing().id !== action.id) return lo;
                    return lo.creating("garbage");
                });
                return state; // No need to run workflow
            }

            case SuperRecordActions.CREATED: {
                if (!state.active) return state;
                state = mapIdPairs(state, lo => {
                    if (!lo.hasValue()) return lo;
                    if (lo.getValueEnforcing().id !== action.id) return lo;
                    return lo.done().map(v => ({
                        ...v,
                        id: action.data.id,
                    }));
                });
                return __workflow(state);
            }

            case MappingActions.__PROCESS_NEXT_IN_QUEUE: {
                if(!Array.isArray(state.queue)) {
                    throw Error("Mapping queue expected to be an array.");
                }
                if(!state.queue.length) {
                    throw Error("Mapping queue should have finished. Something went wrong with the queue.");
                }
                if(typeof state.processQueueCallback !== "function") {
                    throw Error("Mapping queue should have a processQueueCallback function to process the remaining queue.");
                }
                const newState = state.processQueueCallback(state);
                newState._counter = 0; // reset the counter
                return __workflow(newState);
            }

            case MappingActions.__DO_IT_AGAIN: {
                return __workflow(state);
            }

            case MappingActions.IT_IS_DONE: {
                return this.getInitialState();
            }

            case MergeActions.CANCEL:
            case MapDialogActions.CANCEL:
            case SuperRecordActions.CANCEL_FOR_OPTLOCK: {
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                ]);
                return this.getInitialState();
            }

            case MergeActions.CANCEL_FOR_SOURCE_OPTLOCK: {
                this.__dispatcher.waitFor([
                    MergeStore.getDispatchToken(),
                ]);
                return {
                    ...state,
                    exclusions: {},
                };
            }

            case SuperRecordActions.UPDATE_ERROR: {
                if(!state.active) return state;
                if(MergeStore.getPluginKey() !== MERGE_PLUGINS.GLOBAL_MERGE) return state;
                // error happened during global merge
                // reset the active state so they can try again.
                return {...state, active: false};
            }

            default: {
                return state;
            }
        }
    }

    isReady() {
        return isReady(this.getState());
    }

    isInteractive() {
        const state = this.getState();
        invariant(state.active, "Asking about interactivity w/o an active mapping is forbidden.");
        return !!state.interactive;
    }

}

// these are NOT contextually aware - they only checks the state of this field,
// not any records/fields it is within.
export const toggleExclusion = (state, recordId, fieldKey, optionalItemKey) =>
    dotProp.set(state, [
            "exclusions",
            recordId,
            fieldKey,
            optionalItemKey || ENTIRE_FIELD,
        ], v =>
            typeof v === "object"
                ? true // it didn't exist
                : !v, // reverse it
    );

export const setExclusion = (state, recordId, columnKey, optionalItemKey, value) => {
    const curr = isExcluded(state, recordId, columnKey, optionalItemKey);
    if (value && curr) return state;
    if (!value && !curr) return state;
    return toggleExclusion(state, recordId, columnKey, optionalItemKey);
};

export const isExcluded = (state, recordId, columnKey, optionalItemKey) => {
    const es = dotProp.get(state, [
        "exclusions",
        recordId,
        columnKey,
        optionalItemKey || ENTIRE_FIELD,
    ]);
    return es == null ? false : es;
};

MappingStore.stateTypes = {
    active: PropTypes.bool,
    initiatingContext: PropTypes.string,
    interactive: PropTypes.bool,
    pluginKey: PropTypes.string,
    target: PropTypes.instanceOf(EntityProxy),
    source: PropTypes.instanceOf(EntityProxy),
    modelLO: loadObjectOf(PropTypes.any), // only want the LO, not the value
    drawerOpen: PropTypes.bool,
    exclusions: PropTypes.objectOf( // record id (target)
        PropTypes.objectOf( // field key
            PropTypes.objectOf( // target item ID or ENTIRE_FIELD
                PropTypes.bool // flag
            ),
        ),
    ),
    idPairs: PropTypes.objectOf( // record id (source)
        PropTypes.objectOf( // entity- (compositions) or record- (associations) qualified field key (target)
            loadObjectOf(PropTypes.shape({
                key: EntityName.propType.isRequired,
                id: recordIdType.isRequired,
                global: PropTypes.bool,
            })),
        ),
    ),
    // internal
    helperToken: PropTypes.string,
    queue: PropTypes.arrayOf(PropTypes.shape({
        target: PropTypes.instanceOf(EntityProxy),
        source: PropTypes.instanceOf(EntityProxy),
    })),
    processQueueCallback: PropTypes.func,
    _counter: PropTypes.number,
};

const rawStore = new MappingStore();
export default typeCheckingStore(rawStore);



// WEBPACK FOOTER //
// ./app/data/MappingStore.js